import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Grid,
  Stack
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const TextConditionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito curto!")
    .max(50, "Muito longo!")
    .required("Digite um nome!"),
  keywords: Yup.string()
    .min(1, "Digite pelo menos uma palavra!")
    .required("Digite as palavras-chave!"),
  matchType: Yup.string()
    .oneOf(["contains", "exact", "startsWith", "endsWith"], "Tipo inválido")
    .required("Selecione o tipo de correspondência"),
});

const FlowBuilderTextConditionModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    keywords: "",
    matchType: "contains",
    caseSensitive: false,
  };

  const [activeModal, setActiveModal] = useState(false);
  const [labels, setLabels] = useState({
    title: "Adicionar Condição de Texto",
    btn: "Adicionar",
  });

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Condição de Texto",
        btn: "Salvar",
      });
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Condição de Texto",
        btn: "Adicionar",
      });
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = (values) => {
    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: values,
      });
    } else if (open === "create") {
      handleClose();
      onSave(values);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Formik
          initialValues={data?.data || initialState}
          enableReinitialize={true}
          validationSchema={TextConditionSchema}
          onSubmit={handleSave}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                  <Grid style={{ width: "100%", marginTop: 40 }} container spacing={2}>
                    <Grid item xs={12}>
                      <Typography>Nome da Condição</Typography>
                      <Field
                        as={TextField}
                        name="name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        placeholder="Ex: Verificar se é 'sim'"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography>Tipo de Correspondência</Typography>
                      <FormControl fullWidth margin="dense" variant="outlined">
                        <Field
                          as={Select}
                          name="matchType"
                          style={{ width: "95%" }}
                        >
                          <MenuItem value="contains">
                            Contém (a palavra está em qualquer lugar)
                          </MenuItem>
                          <MenuItem value="exact">
                            Exato (a mensagem é exatamente a palavra)
                          </MenuItem>
                          <MenuItem value="startsWith">
                            Começa com (a mensagem começa com a palavra)
                          </MenuItem>
                          <MenuItem value="endsWith">
                            Termina com (a mensagem termina com a palavra)
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography>Palavras-chave</Typography>
                      <Field
                        as={TextField}
                        name="keywords"
                        error={touched.keywords && Boolean(errors.keywords)}
                        helperText={touched.keywords && errors.keywords || "Separe múltiplas palavras com vírgula (ex: sim, yes, ok)"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="sim, yes, ok"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.caseSensitive}
                            onChange={(e) =>
                              setFieldValue("caseSensitive", e.target.checked)
                            }
                          />
                        }
                        label="Diferenciar maiúsculas/minúsculas"
                      />
                    </Grid>
                  </Grid>
                </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderTextConditionModal;
